/**
 * Every piece of unsaved work on the page, by id. `leave` entries come from the leave guard and
 * make leaving the page ask; others only make switching team ask. `discard` throws the work away
 * for good (drafts kept in storage), once the person has agreed to lose it.
 */
export type UnsavedChanges = { leave?: boolean; message?: string; discard?: () => void };

const blockers = new Map<string, UnsavedChanges>();
let pageUnloadApproved = false;

/** `false` removes the entry; `true` registers one with nothing extra. */
export function setUnsavedChangesBlocker(id: string, entry: UnsavedChanges | boolean): void {
  if (entry === false) blockers.delete(id);
  // Map.set on an existing id keeps its place, so the first page's wording stays first.
  else blockers.set(id, entry === true ? {} : entry);
}

/**
 * The entry a page's leave guard keeps, or null for none. While a save is in flight, leaving may go
 * ahead, but switching team still asks: its reload would lose the work if the save then fails.
 */
export function unsavedGuardEntry(dirty: boolean, saving: boolean): UnsavedChanges | null {
  if (!dirty) return null;
  return saving ? {} : { leave: true };
}

/** Changes the wording of an entry that is already registered; does nothing otherwise. */
export function setUnsavedChangesMessage(id: string, message: string): void {
  const entry = blockers.get(id);
  if (entry) blockers.set(id, { ...entry, message });
}

export function hasUnsavedChanges(scope: 'any' | 'leave' = 'any'): boolean {
  if (scope === 'any') return blockers.size > 0;
  for (const entry of blockers.values()) if (entry.leave) return true;
  return false;
}

/** The wording of the first guard that asks before leaving, if it has its own. */
export function unsavedLeaveMessage(): string | undefined {
  for (const entry of blockers.values()) if (entry.leave) return entry.message;
  return undefined;
}

/**
 * Only asks; nothing is thrown away here, because the switch can still fail. Call
 * `discardUnsavedChanges` once it has gone through. `confirm` is the app's modal, passed in so
 * this file stays free of UI.
 */
export async function confirmDiscardUnsavedChanges(
  confirm: (message: string) => boolean | Promise<boolean>,
  message = 'Discard unsaved changes and switch team?'
): Promise<boolean> {
  return !hasUnsavedChanges() || (await confirm(message));
}

/** Runs every entry's discard. Entries stay registered; their pages remove them on unmount. */
export function discardUnsavedChanges(): void {
  for (const [id, entry] of blockers) {
    try {
      entry.discard?.();
    } catch (error) {
      console.error(`Could not discard unsaved changes (${id}):`, error);
    }
  }
}

type PageAddress = { origin: string; pathname: string; search: string };

/** Whether going to `target` leaves the current page: same site, another path or query. A hash alone stays. */
export function leavesPage(target: PageAddress, current: PageAddress): boolean {
  if (target.origin !== current.origin) return false;
  return target.pathname !== current.pathname || target.search !== current.search;
}

/**
 * The person has already agreed to lose any unsaved work, so the guards let the page unload
 * without asking again. One way until the page reloads: call it only right before a full-page
 * navigation, or every guard in the tab stays silent.
 */
export function approvePageUnload(): void {
  pageUnloadApproved = true;
}

/** Takes `approvePageUnload` back, when the full-page navigation it was for didn't happen. */
export function withdrawPageUnloadApproval(): void {
  pageUnloadApproved = false;
}

export function isPageUnloadApproved(): boolean {
  return pageUnloadApproved;
}

export function clearUnsavedChangesBlockersForTest(): void {
  blockers.clear();
  pageUnloadApproved = false;
}
